import { useEffect, useState } from 'preact/hooks';
import { Gift, Mail, MailOpen, Trash } from 'lucide-preact';
import { claimInboxMessage, deleteInboxMessage, loadInbox, readInboxMessage } from '../state/actions';
import { inbox } from '../state/store';
import { timeAgo } from '../ui/format';
import { useAsync } from '../ui/hooks';
import { AsyncButton, confirmDialog, Countdown, Empty, ErrorState, OnlineOnly, RewardChips, Screen, Spinner } from '../ui/kit';

function hasRewards(r: { coins?: number; gems?: number; xp?: number; items?: unknown[] } | null): boolean {
  return !!r && !!(r.coins || r.gems || r.xp || r.items?.length);
}

export function InboxScreen() {
  const load = useAsync(() => loadInbox(), []);
  const list = inbox.value;
  const [openId, setOpenId] = useState<string | null>(null);
  useEffect(() => {
    if (!openId && list?.length) {
      setOpenId(list[0].id);
      void readInboxMessage(list[0].id);
    }
  }, [list?.length]);
  const msg = list?.find((m) => m.id === openId) ?? null;
  return (
    <Screen title="Inbox" icon={<Mail size={26} />}>
      <OnlineOnly feature="Your inbox">
        {!list ? (
          load.error ? (
            <ErrorState message={load.error} onRetry={load.reload} />
          ) : (
            <Spinner label="Checking your mail…" />
          )
        ) : !list.length ? (
          <Empty icon={<MailOpen size={44} />} title="All caught up!" body="Rewards, news and gifts from the Voxel Marines team land here." />
        ) : (
          <div class="split inbox">
            <div class="split-main scr-scroll inbox-list">
              {list.map((m) => (
                <button
                  key={m.id}
                  class={`inbox-row ${m.id === openId ? 'is-on' : ''} ${m.read ? '' : 'is-unread'}`}
                  onClick={() => {
                    setOpenId(m.id);
                    void readInboxMessage(m.id);
                  }}
                >
                  <span class="inbox-row-icon">{hasRewards(m.rewards) && !m.claimed ? <Gift size={22} /> : m.read ? <MailOpen size={22} /> : <Mail size={22} />}</span>
                  <span class="inbox-row-text">
                    <b>{m.title}</b>
                    <small>{timeAgo(m.createdAt)}</small>
                  </span>
                  {!m.read && <span class="ui-dot" />}
                </button>
              ))}
            </div>
            <div class="split-side scr-scroll inbox-detail">
              {msg && (
                <div class="inbox-msg" key={msg.id}>
                  <h2>{msg.title}</h2>
                  <div class="inbox-msg-meta">
                    <span>{timeAgo(msg.createdAt)}</span>
                    {msg.expiresAt && <Countdown to={msg.expiresAt} prefix="Expires in " />}
                  </div>
                  <div class="inbox-msg-body">
                    {msg.body.split(/\n+/).map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                  {hasRewards(msg.rewards) && (
                    <div class="inbox-rewards panel">
                      <h4>
                        <Gift size={18} /> Attached rewards
                      </h4>
                      <RewardChips reward={msg.rewards} />
                      {msg.claimed ? <span class="muted">Claimed</span> : <AsyncButton class="primary" onClick={() => claimInboxMessage(msg.id)}>Claim rewards</AsyncButton>}
                    </div>
                  )}
                  <div class="inbox-msg-actions">
                    <AsyncButton
                      class="sm"
                      onClick={async () => {
                        if (hasRewards(msg.rewards) && !msg.claimed && !(await confirmDialog({ title: 'Delete without claiming?', body: 'The rewards in this message will be lost.', confirm: 'Delete', danger: true }))) return;
                        await deleteInboxMessage(msg.id);
                        setOpenId(null);
                      }}
                    >
                      <Trash size={16} /> Delete
                    </AsyncButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </OnlineOnly>
    </Screen>
  );
}
